import { http } from './http';
import { API_BASE_URL, ROUTES } from './config';
import { errorFromResponse } from './errors';
import { tokenStore } from './tokenStore';
import type {
  CheckInRequest,
  CheckInResult,
  NearbyWorker,
  PublicWorker,
  Shift,
  SubmitRatingRequest,
  UpdateWorkerProfileRequest,
  Worker,
  WorkerAdminDetail,
  WorkerDashboard,
  WorkerJobsPage,
  WorkerMe,
  WorkerPayout,
  WorkerRatingsSummary,
  WorkerVerification,
} from '../types/domain';

/** WorkerService client (worker|manager). */
export const workersApi = {
  /** `includeInactive` is for Module 3's admin oversight list — the default (false) preserves
   * existing dispatcher-tooling callers, who only ever want active workers. */
  list(includeInactive = false): Promise<Worker[]> {
    return http.get(`${ROUTES.workers}${includeInactive ? '?includeInactive=true' : ''}`);
  },

  /** Manager+ admin detail: profile + real job/earnings stats (Module 3). */
  adminDetail(workerId: string): Promise<WorkerAdminDetail> {
    return http.get(`${ROUTES.workers}/${workerId}`);
  },

  /** Manager+: one worker's real job history (Module 3) — same shape as `myJobs`, scoped by an
   * explicit workerId instead of the caller's own resolved worker. */
  jobsFor(workerId: string, status: 'upcoming' | 'completed' = 'upcoming'): Promise<WorkerJobsPage> {
    return http.get(`${ROUTES.workers}/${workerId}/jobs?status=${status}`);
  },

  /** Manager+: deactivate/reactivate. History is kept — this only flips a flag. */
  setActive(workerId: string, active: boolean): Promise<{ id: string; active: boolean }> {
    return http.put(`${ROUTES.workers}/${workerId}/active`, { active });
  },

  /** The signed-in worker's own profile. 404s if no Worker record is linked to this account yet. */
  me(): Promise<WorkerMe> {
    return http.get(`${ROUTES.workers}/me`);
  },

  /** Worker Dashboard Overview: upcoming/completed job counts, earnings, next jobs. */
  myDashboard(): Promise<WorkerDashboard> {
    return http.get(`${ROUTES.workers}/me/dashboard`);
  },

  /** "Booking Requests" reconciled to the dispatch model — see WorkerJobsPage. */
  myJobs(status: 'upcoming' | 'completed' = 'upcoming'): Promise<WorkerJobsPage> {
    return http.get(`${ROUTES.workers}/me/jobs?status=${status}`);
  },

  updateMe(body: UpdateWorkerProfileRequest): Promise<WorkerMe> {
    return http.put(`${ROUTES.workers}/me`, body);
  },

  /** Anonymous "Find Cleaners" search — no auth required. */
  publicSearch(params: { service?: string; areaCode?: string } = {}): Promise<PublicWorker[]> {
    const q = new URLSearchParams();
    if (params.service) q.set('service', params.service);
    if (params.areaCode) q.set('areaCode', params.areaCode);
    const qs = q.toString();
    return http.get(`${ROUTES.workers}/public/search${qs ? `?${qs}` : ''}`);
  },

  /** Customer-only: rate a completed job. 409s if that job was already rated. */
  submitRating(body: SubmitRatingRequest): Promise<{ id: string; workerId: string; stars: number; review: string | null }> {
    return http.post(`${ROUTES.workers}/ratings`, body);
  },

  /** Anonymous — average/breakdown/reviews for one worker. */
  ratingsFor(workerId: string): Promise<WorkerRatingsSummary> {
    return http.get(`${ROUTES.workers}/public/${workerId}/ratings`);
  },

  create(body: Partial<Worker>): Promise<{ id: string }> {
    return http.post(ROUTES.workers, body);
  },

  nearby(lat: number, lng: number, radiusMeters = 5000): Promise<NearbyWorker[]> {
    return http.get(`${ROUTES.workers}/nearby?lat=${lat}&lng=${lng}&radius=${radiusMeters}`);
  },

  /** Mobile "Today" screen: a worker's ordered stops for a date. */
  route(workerId: string, date: string): Promise<{ date: string; stops: unknown[] }> {
    return http.get(`${ROUTES.workers}/${workerId}/route?date=${encodeURIComponent(date)}`);
  },

  listShifts(workerId: string, from?: string): Promise<Shift[]> {
    const q = from ? `?from=${encodeURIComponent(from)}` : '';
    return http.get(`${ROUTES.workers}/${workerId}/shifts${q}`);
  },

  createShift(workerId: string, body: { startsAt: string; endsAt: string; notes?: string }): Promise<{ shiftId: string }> {
    return http.post(`${ROUTES.workers}/${workerId}/shifts`, body);
  },

  startShift(shiftId: string): Promise<void> {
    return http.post(`${ROUTES.workers}/shifts/${shiftId}/start`);
  },

  completeShift(shiftId: string): Promise<void> {
    return http.post(`${ROUTES.workers}/shifts/${shiftId}/complete`);
  },

  cancelShift(shiftId: string): Promise<void> {
    return http.post(`${ROUTES.workers}/shifts/${shiftId}/cancel`);
  },

  checkIn(body: CheckInRequest): Promise<CheckInResult> {
    return http.post(ROUTES.checkins, body);
  },

  checkOut(checkInId: string): Promise<void> {
    return http.post(`${ROUTES.checkins}/${checkInId}/checkout`);
  },

  myVerification(): Promise<WorkerVerification> {
    return http.get(`${ROUTES.workers}/me/verification`);
  },

  /** multipart/form-data — bypasses the JSON `http` client, same as imagesApi.upload(). */
  async submitVerification(file: File): Promise<{ status: string; verificationSubmittedAt: string }> {
    const form = new FormData();
    form.append('file', file);

    const token = tokenStore.get()?.accessToken;
    const response = await fetch(`${API_BASE_URL}${ROUTES.workers}/me/verification`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!response.ok) {
      const problem = await response.json().catch(() => null);
      throw errorFromResponse(response.status, problem, response.headers.get('X-Correlation-Id'));
    }
    return response.json();
  },

  /** Manager+: approve or reject a pending verification. */
  reviewVerification(workerId: string, body: { approve: boolean; rejectionReason?: string }): Promise<{ status: string }> {
    return http.put(`${ROUTES.workers}/${workerId}/verification`, body);
  },

  myPayouts(): Promise<WorkerPayout[]> {
    return http.get(`${ROUTES.workers}/me/payouts`);
  },

  /** Manager+: one worker's payout history. */
  payoutsFor(workerId: string): Promise<WorkerPayout[]> {
    return http.get(`${ROUTES.workers}/${workerId}/payouts`);
  },

  /** Manager+: computes and snapshots a payout from real CheckIn durations in the period. */
  createPayout(workerId: string, periodStart: string, periodEnd: string): Promise<WorkerPayout> {
    return http.post(`${ROUTES.workers}/${workerId}/payouts`, { periodStart, periodEnd });
  },

  /** Manager+: marks a payout Paid, notifies the worker live. */
  releasePayout(payoutId: string): Promise<{ id: string; status: string; paidAt: string }> {
    return http.post(`${ROUTES.workers}/payouts/${payoutId}/release`);
  },

  /** Live workforce tracking (Prompt 2.4/8.1) — not persisted, just relayed to dispatchers via
   * WorkerHub's OnLocationUpdated. Call periodically while checked in. */
  pingLocation(latitude: number, longitude: number): Promise<void> {
    return http.post(`${ROUTES.workers}/me/location`, { latitude, longitude });
  },
};
